import type { HierarchyCircularNode } from 'd3-hierarchy';

import type { ExplorationVolume } from '../../types/exploration';
import type { HierarchyNode } from '../../types/exploration';

/** Fields read from volume nodes on the bubble canvas (emphasis, tooltips, drill). */
export type PackNodeVolume = Pick<
  ExplorationVolume,
  'ebsId' | 'instanceId' | 'status' | 'supportStatus' | 'activationStatus'
>;

export interface PackNodeData {
  type: string;
  key?: string;
  name?: string;
  volume?: PackNodeVolume;
  value?: number;
  count?: number;
  _isOverflow?: boolean;
  _overflowParentKey?: string;
  _hidden?: number;
  /** Capped sibling subtrees — used so sidebar highlight can match `+N more`. */
  _overflowChildren?: PackNodeData[];
  /** Hierarchy children (present on overflow subtrees and builder nodes). */
  children?: PackNodeData[] | null;
  _volumes?: PackNodeVolume[];
}

export interface PackNode {
  data: PackNodeData;
  x?: number;
  y?: number;
  r?: number;
  depth?: number;
  parent?: PackNode | null;
  children?: PackNode[];
  ancestors?: () => PackNode[];
  leaves?: () => PackNode[];
}

/** D3 pack() output — x/y/r are required after layout. */
export type PackLayoutNode = HierarchyCircularNode<HierarchyNode>;

export interface BubblePos {
  cx: number;
  cy: number;
  r: number;
}

export type BubblePosCache = Map<string, BubblePos>;

/** Structural hierarchy nodes that participate in drill navigation. */
export const DRILLABLE_STRUCTURAL_TYPES = new Set([
  'account',
  'cluster',
  'ec2',
  'unattached',
  'region',
  'tag',
  'nodeGroup',
  'instance',
]);

export const DRILLABLE_NODE_TYPES = new Set([
  ...DRILLABLE_STRUCTURAL_TYPES,
  'status',
  'reason',
]);

/** Bubble types that share volume dim palette (fill/stroke) and readable text dim. */
export const VOLUME_DOMAIN_TYPES = new Set(['volume', 'status', 'reason']);

export function isVolumeDomainType(type: string | undefined | null): boolean {
  return type != null && VOLUME_DOMAIN_TYPES.has(type);
}
